import { useCallback, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ProductFeatureName } from 'dashboard/models/organization';
import { ReportTemplate, ReportTemplateStatus } from 'dashboard/models/report-template';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import {
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/actions/report-templates';
import { RootState } from 'dashboard/redux/store';
import { updateStripePaymentMethod } from 'dashboard/redux/thunk-actions/organization';
import {
  createOrganizationPlanMigration,
  loadAvailablePlans,
} from 'dashboard/redux/thunk-actions/organization-plans';
import { createReportTemplate } from 'dashboard/redux/thunk-actions/report-template';
import {
  loadReportTemplatesFilterOptions,
  loadReportTemplatesList,
} from 'dashboard/redux/thunk-actions/report-templates';
import DashboardReportTemplatesView from 'dashboard/views/DashboardReportTemplatesView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardReportTemplatesRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.loadReportTemplatesList,
      props.currentPage,
      props.pageSize,
      props.filters,
      props.sortParams,
    ],
  );

  const {
    handlePageLinkClick,
    handlePageSizeClick,
    handleSortUpdate,
    handleOmniSearch,
    isReady: isTableStateReady,
  } = useTable({
    filters: props.filters,
    sortParams: props.sortParams,
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setFilters: props.setFilters,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
    setSortParams: props.setSortParams,
  });

  const { loading: loadingReportTemplates, callback: reportTemplateLoader } = useApiLoader({
    loader: () => {
      if (!isTableStateReady) {
        return Promise.resolve();
      }

      return props.loadReportTemplatesList({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: props.pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: props.sortParams,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: props.filters,
        signal: abortController.signal,
      });
    },
    deps: [
      props.loadReportTemplatesList,
      props.currentPage,
      props.pageSize,
      props.sortParams,
      props.filters,
      isTableStateReady,
    ],
    onError: (error) => {
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load report templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadReportTemplatesFilterOptions } = useApiLoader({
    loader: async () => {
      if (!isTableStateReady) {
        return;
      }

      await props.loadReportTemplatesFilterOptions({
        filters: props.filters,
        sortParams: props.sortParams,
      });
    },
    deps: [
      props.loadReportTemplatesFilterOptions,
      props.filters,
      props.sortParams,
      isTableStateReady,
    ],
    onError: (_) => {
      props.showMessage('Failed to load template filters.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    callback: handleCreateReportTemplate,
    loading: handleCreateReportTemplateLoading,
    error: handleCreateReportTemplateError,
  } = useApiCallback(
    async (reportTemplateBuilderName: string) => {
      const reportTemplate = await props.createReportTemplate(reportTemplateBuilderName);
      props.showMessage('Your template was successfully created.', MessageType.Success);
      navigate(`/dashboard/report-templates/${reportTemplate.id}`);
    },
    [props.createReportTemplate],
  );

  const { error: archiveReportTemplateError, callback: archiveReportTemplate } = useApiCallback(
    async (reportTemplate: ReportTemplate) => {
      await SessionClientDashboard.deleteResource<ReportTemplate>(
        ApiEndpoint.ReportTemplate,
        reportTemplate.id,
      );
      reportTemplateLoader();
      props.showMessage('Successfully archived report template.', MessageType.Success);
    },
    [reportTemplateLoader],
  );

  useApiErrorHandler({
    error: handleCreateReportTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create template. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: archiveReportTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to archive report template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to archive report template, please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const handleStatusFilterUpdate = useCallback((status: ReportTemplateStatus[]) => {
    props.setFilters({ status });
    handlePageLinkClick(1);
  }, []);

  const handleTypeFilterUpdate = useCallback((type: ReportTemplate['type'][]) => {
    props.setFilters({ type });
    handlePageLinkClick(1);
  }, []);

  const loading =
    loadingReportTemplates ||
    loadReportTemplatesFilterOptions ||
    handleCreateReportTemplateLoading ||
    !isTableStateReady;

  const { callback: handleLoadAvailablePlans, loading: loadingAvailablePlans } = useApiCallback(
    async (requiredProductFeatures: ProductFeatureName[]) => {
      return await props.loadAvailablePlans(requiredProductFeatures);
    },
    [],
  );

  const { callback: updateStripePaymentMethodId, error: updateStripePaymentMethodError } =
    useApiCallback(async (paymentMethodId: string) => {
      props.clearMessages();
      await props.updateStripePaymentMethod(paymentMethodId);
      props.showMessage('Billing information successfully updated.', MessageType.Success);
    }, []);

  useApiErrorHandler({
    error: updateStripePaymentMethodError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Billing information failed to update. Please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  const { error: handleCreatePlanMigrationError, callback: handleCreateOrganizationPlanMigration } =
    useApiCallback(async (inquiryId: string, requestedPlanKey: string) => {
      const { organizationPlanMigration } = await props.createOrganizationPlanMigration(
        inquiryId,
        requestedPlanKey,
      );
      return organizationPlanMigration;
    }, []);

  useApiErrorHandler({
    error: handleCreatePlanMigrationError,
    onError: () => props.showMessage('Failed to request new plan.', MessageType.Failure, false),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  return (
    <DashboardReportTemplatesView
      isLoading={loading}
      reportTemplates={props.reportTemplates}
      reportTemplateFilterOptions={props.reportTemplateFilterOptions}
      loadingAvailablePlans={loadingAvailablePlans}
      featureFlags={props.featureFlags}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={props.sortParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={props.filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={props.pageSize}
      handleCreateReportTemplate={handleCreateReportTemplate}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleSortUpdate={handleSortUpdate}
      handleOmniSearch={handleOmniSearch}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleTypeFilterUpdate={handleTypeFilterUpdate}
      archiveReportTemplate={archiveReportTemplate}
      handleLoadAvailablePlans={handleLoadAvailablePlans}
      handleCreateOrganizationPlanMigration={handleCreateOrganizationPlanMigration}
      updatePayment={updateStripePaymentMethodId}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    reportTemplates: state.reportTemplates.reportTemplates,
    reportTemplateFilterOptions: state.reportTemplates.filterOptions,
    featureFlags: state.featureFlags.featureFlags,
    paginationParams: state.reportTemplates.paginationParams,
    sortParams: state.reportTemplates.sortParams,
    filters: state.reportTemplates.filters,
    currentPage: state.reportTemplates.currentPage,
    pageSize: state.reportTemplates.pageSize,
    organization: state.organization.organization,
  };
};
const mapDispatchToProps = {
  clearMessages,
  createOrganizationPlanMigration,
  createReportTemplate,
  loadAvailablePlans,
  loadReportTemplatesList,
  loadReportTemplatesFilterOptions,
  setSortParams,
  setFilters,
  setCurrentPage,
  setPageSize,
  showMessage,
  updateStripePaymentMethod,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardReportTemplatesRoute);
